/**
 * 단계 상세 모달 (v3)
 * — 구비서류 표 + 기관/신청/처리/수수료 + 실무 주의사항 + 출처 링크
 * — 외부 라이브러리 X. backdrop + 중앙 박스 + Esc/외부클릭 닫힘.
 */
import { useEffect, useRef } from 'react';
import type { Step } from './steps';

interface Props {
  step: Step;
  onClose: () => void;
}

export function StepDetailDialog({ step, onClose }: Props) {
  const { detail, label, name } = step;
  const backdropRef = useRef<HTMLDivElement>(null);

  // Esc 키 닫힘
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // 스크롤 잠금
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  if (!detail) return null;

  function handleBackdropClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === backdropRef.current) onClose();
  }

  const reqBadge = (req: string) => {
    if (req === '필수') return 'bg-fg text-surface';
    if (req === '필수(법인)') return 'bg-fg-subtle text-surface';
    return 'bg-bdr-subtle text-fg-subtle';
  };

  return (
    <div
      ref={backdropRef}
      onClick={handleBackdropClick}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      aria-modal="true"
      role="dialog"
      aria-label={`${name} 상세`}
    >
      <div className="relative bg-surface rounded-2xl border border-bdr-subtlest w-full max-w-lg max-h-[85vh] flex flex-col shadow-xl">

        {/* 헤더 */}
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b border-bdr-subtlest shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="inline-flex items-center rounded-md text-[10px] font-bold px-2 py-0.5 leading-none bg-fg text-surface shrink-0">
              {label}
            </span>
            <h2 className="text-base font-bold leading-snug truncate">{name}</h2>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg text-fg-subtler hover:text-fg hover:bg-bdr-subtlest transition-colors"
            aria-label="닫기"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* 본문 (스크롤 영역) */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-5">

          {/* 메타 정보 */}
          <dl className="space-y-2">
            <MetaRow label="기관" value={detail.agency} />
            {detail.applicationMethod && (
              <MetaRow label="신청" value={detail.applicationMethod} />
            )}
            {detail.processingTime && (
              <MetaRow label="처리" value={detail.processingTime} />
            )}
            {detail.fee && (
              <MetaRow label="수수료" value={detail.fee} />
            )}
          </dl>

          {/* 구비서류 표 */}
          {detail.documents && detail.documents.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-fg-subtle mb-2">구비서류</p>
              <div className="rounded-xl border border-bdr-subtlest overflow-hidden">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-surface-alt">
                      <th className="text-left px-3 py-2 font-semibold text-fg-subtle w-[46%]">서류</th>
                      <th className="text-left px-3 py-2 font-semibold text-fg-subtle w-[16%]">구분</th>
                      <th className="text-left px-3 py-2 font-semibold text-fg-subtle">비고</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.documents.map((doc, i) => (
                      <tr
                        key={i}
                        className={i % 2 === 0 ? '' : 'bg-surface-alt/50'}
                        style={{ borderTop: '1px solid var(--color-bdr-subtlest)' }}
                      >
                        <td className="px-3 py-2 text-fg leading-snug">{doc.name}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold leading-none ${reqBadge(doc.required)}`}>
                            {doc.required}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-fg-subtler leading-snug">{doc.condition ?? '–'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 선행 절차 */}
          {detail.prerequisites && detail.prerequisites.length > 0 && (
            <div className="rounded-xl border border-bdr-subtle bg-surface-alt px-4 py-3 space-y-2">
              <p className="text-xs font-semibold text-fg-subtle">선행 절차</p>
              <ul className="space-y-2">
                {detail.prerequisites.map((pre, i) => (
                  <li key={i} className="flex gap-2 text-xs leading-relaxed">
                    <span className="shrink-0 font-semibold text-fg mt-0.5 min-w-[6rem]">{pre.what}</span>
                    <span className="text-fg-subtle">{pre.detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 실무 주의사항 */}
          {detail.notes && detail.notes.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-fg-subtle mb-2">실무 주의사항</p>
              <ul className="space-y-1.5">
                {detail.notes.map((note, i) => (
                  <li key={i} className="flex gap-2 text-xs text-fg-subtle leading-relaxed">
                    <span className="shrink-0 text-fg-subtler mt-0.5">•</span>
                    <span>{note}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 출처 */}
          {detail.references && detail.references.length > 0 && (
            <div className="border-t border-bdr-subtlest pt-4">
              <p className="text-[10px] font-semibold text-fg-subtler mb-1.5 uppercase tracking-wide">출처</p>
              <div className="flex flex-wrap gap-2">
                {detail.references.map((ref, i) => (
                  <a
                    key={i}
                    href={ref.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-fg-subtle underline underline-offset-2 hover:text-fg transition-colors"
                  >
                    {ref.label}
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true" className="shrink-0 opacity-60">
                      <path d="M1.5 8.5l7-7M4 1.5H8.5V6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2 text-xs leading-relaxed">
      <dt className="shrink-0 w-12 text-fg-subtler font-medium">{label}</dt>
      <dd className="text-fg">{value}</dd>
    </div>
  );
}
